/**
 * The three-pane shell's resizable primitives (`110-design-system.md` §5).
 *
 * DEVIATION, recorded on purpose: shadcn's registry `resizable` is written against
 * react-resizable-panels v2/v3 (`PanelGroup` / `PanelResizeHandle`, percentage sizes
 * only). We are on v4, whose API is `Group` / `Panel` / `Separator` and — the reason
 * it is worth the divergence — whose sizes can be **pixels**. §5's table is written in
 * pixels (260 / 180 / 480), so v4 lets us implement it literally instead of converting
 * a px table into viewport percentages that drift with the window width.
 *
 * This file is the same kind of thing the registry copy is: a thin styled wrapper over
 * the library. Behaviour lives in the shell, not here.
 */
import { Group, Panel, Separator } from 'react-resizable-panels';
import { cn } from '@/lib/utils';

export { usePanelRef, type PanelImperativeHandle, type PanelSize } from 'react-resizable-panels';

export function ResizableGroup({ className, ...props }: React.ComponentProps<typeof Group>) {
  return <Group className={cn('flex h-full w-full', className)} {...props} />;
}

export function ResizablePanel(props: React.ComponentProps<typeof Panel>) {
  return <Panel {...props} />;
}

/** 110 §10: no transition while dragging. The 1px line is the whole affordance. */
export function ResizableSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn(
        'relative w-px shrink-0 bg-[var(--border)] outline-none',
        'after:absolute after:inset-y-0 after:-left-1 after:w-2 after:content-[""]',
        'hover:bg-[var(--ring)] data-[state=drag]:bg-[var(--ring)]',
        // `outline-solid` is NOT redundant, and leaving it out is a silent bug.
        //
        // Tailwind v4's `outline-none` does two things: `outline-style: none` AND
        // `--tw-outline-style: none`. Every later `outline-*` utility resolves its style from
        // that variable — so `focus-visible:outline-2` sets the WIDTH and the COLOUR of an
        // outline whose STYLE is still `none`, and the ring never draws. The separator is a
        // `tabindex="0"` element you can resize with the arrow keys; it was reachable by Tab
        // and completely invisible when it got there (`110` §11: "Focus is always visible.
        // Never `outline: none` without a replacement"). Caught by tabbing the app (M7,
        // criterion 9).
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-solid focus-visible:outline-[var(--ring)]',
        className,
      )}
      {...props}
    />
  );
}
